import ComponentManager from "../../componentFramework";
import JsfAjaxScrollImprovements from "../../scripts/jsf/JsfAjaxScrollImprovements";
import Debouncer from "../../scripts/Debouncer";
import CancellableQueue from "../../scripts/CancellableQueue";
import eventListenerOption from "../../scripts/eventListenerOption";
import JsfAjax from "../../scripts/jsf/JsfAjax";

/**
 * Uses JsfAjaxScrollImprovements to make the search results retain scroll position
 * after an AJAX update and/or ensure a selected item is in view.
 */
@ComponentManager.register()
export default class PanelLayout {
	public static componentStyleClass = "panelLayout";
	private static readonly mainContentStyleClass_disableOverflow = "panelLayout__mainContent--disableOverflow";

	private jsfAjaxScrollImprovements: JsfAjaxScrollImprovements | undefined;
	private readonly updateLayoutDebounced: Debouncer<[]>;
	private readonly cancellableQueue: CancellableQueue = new CancellableQueue();

	public static getInstance(element: Element | null, selfOrAncestor: boolean = true): PanelLayout | undefined {
		if (element === null) {
			return undefined;
		}
		const instanceElement = selfOrAncestor ?
			element.closest<HTMLElement>("." + PanelLayout.componentStyleClass)
			: element.querySelector<HTMLElement>("." + PanelLayout.componentStyleClass);
		return instanceElement === null ? undefined : ComponentManager.getComponent(instanceElement, PanelLayout);
	}

	constructor(private readonly rootElement: HTMLElement) {
		this.updateLayoutDebounced = new Debouncer({callback: this.updateLayout, queue: this.cancellableQueue});
	}

	@ComponentManager.on("ready")
	private componentReady() {
		// Enhance the scrolling of the sidebar area
		const sidebarContentElement = this.getSidebarContentElement();
		if (sidebarContentElement !== null) {
			const isTreeInSidebar = sidebarContentElement.querySelector(".ui-tree") !== null;
			const selectedItemQuery = isTreeInSidebar ? ".ui-treenode-label.ui-state-highlight" : undefined;

			const updateIds = [sidebarContentElement.id];
			this.jsfAjaxScrollImprovements = new JsfAjaxScrollImprovements(
				this.rootElement.ownerDocument,
				{
					getScrollArea: this.getSidebarContentElement,
					onUpdateIds: updateIds,
					selectedItemQuery: selectedItemQuery,
					preferCenteringSelectedIfSourceNotAncestor: true,
					alwaysRememberScrollPosition: isTreeInSidebar
				}
			);
		}

		// Update layout
		// Removed next line because related in too much layout changes after an AJAX update:
		// 		ElementAndSubtreeObserver.getInstance(this.rootElement.ownerDocument.documentElement).addEventListener("mutation", this.updateLayoutDebounced.run);
		const defaultView = this.rootElement.ownerDocument.defaultView!;
		defaultView.addEventListener("resize", this.updateLayoutDebounced.run, eventListenerOption.passiveNotCapture);
		JsfAjax.getInstance(defaultView).addEventListener("always", this.updateLayoutDebounced.run);
		this.updateLayoutDebounced.runNow(); // Call once
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.cancellableQueue.cancel();
		this.jsfAjaxScrollImprovements?.destroy();
		// Removed next line, see note above related to adding the listener:
		// 		ElementAndSubtreeObserver.getInstance(this.rootElement.ownerDocument.documentElement).removeEventListener("mutation", this.updateLayoutDebounced.run);
		const defaultView = this.rootElement.ownerDocument.defaultView!;
		defaultView.removeEventListener("resize", this.updateLayoutDebounced.run);
		JsfAjax.getInstance(defaultView).removeEventListener("always", this.updateLayoutDebounced.run);
	}

	private getSidebarContentElement = () => {
		return this.rootElement.querySelector(".panelLayout__sidebarContent");
	}

	public getMainBodyElement = () => {
		return this.rootElement.querySelector(".panelLayout__mainBody");
	}
	public getMainContentElement = (): HTMLElement | null => {
		return this.rootElement.querySelector<HTMLElement>(".panelLayout__mainContent");
	}

	private updateLayout = () => {
		// Turn off overflow of the main content area if the height is below a pixel value or less than a percentage of the overall height.
		const mainBodyElem = this.getMainBodyElement();
		const mainContentElem = this.getMainContentElement();
		if (mainBodyElem !== null && mainContentElem !== null) {
			let availableHeight = mainBodyElem.clientHeight;
			const mainBodyElemChildren = mainBodyElem.children;
			for (let i = 0; i < mainBodyElemChildren.length; i++) {
				const elem = mainBodyElemChildren[i];
				if (elem !== mainContentElem && elem instanceof HTMLElement) {
					availableHeight -= elem.offsetHeight;
				}
			}

			const disableOverflow = availableHeight < 600 || availableHeight / mainBodyElem.clientHeight < 0.50;
			mainContentElem.classList.toggle(PanelLayout.mainContentStyleClass_disableOverflow, disableOverflow);
		}
	}

	public isMainContentOverflowDisabled(): boolean {
		const mainContentElem = this.getMainContentElement();
		if (mainContentElem === null) {
			return false; // Should never happen.
		}
		return mainContentElem.classList.contains(PanelLayout.mainContentStyleClass_disableOverflow);
	}
}
